import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AlertCircle, Eye, CheckCircle, XCircle, Ban, Search, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import { getAllReports, updateReport } from '../../services/admin/reportsService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, filters]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const result = await getAllReports({ limit: 1000 });
      
      if (result.success) {
        setReports(result.data.reports);
        toast({ title: 'Success', description: `Loaded ${result.data.reports.length} reports` });
      } else {
        toast({ title: 'Error', description: result.message, variant: 'destructive' });
      }
    } catch (error) {
      console.error('Load reports error:', error);
      toast({ title: 'Error', description: 'Failed to load reports', variant: 'destructive' });
    }
    setLoading(false);
  };

  const filterReports = () => {
    let filtered = [...reports];

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(report => report.status === filters.status);
    }

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(report =>
        report.reason?.toLowerCase().includes(filters.search.toLowerCase()) ||
        report.reported_user_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        report.reported_user_phone?.includes(filters.search)
      );
    }

    // Sort by created date (newest first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredReports(filtered);
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      const status = action === 'resolve' ? 'resolved' : 'dismissed';
      const result = await updateReport(reportId, { status });
      
      if (result.success) {
        // Update local state
        setReports(reports.map(r => 
          r.id === reportId ? { ...r, status } : r
        ));
        
        toast({ 
          title: 'Success', 
          description: result.message || `Report ${action === 'resolve' ? 'resolved' : 'dismissed'} successfully` 
        });
        
        setShowModal(false);
        setSelectedReport(null);
      } else {
        toast({ title: 'Error', description: result.message, variant: 'destructive' });
      }
    } catch (error) {
      console.error('Resolve report error:', error);
      toast({ title: 'Error', description: 'Failed to update report', variant: 'destructive' });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-yellow-500">Pending</Badge>,
      under_review: <Badge className="bg-blue-500">Under Review</Badge>,
      resolved: <Badge className="bg-green-500">Resolved</Badge>,
      dismissed: <Badge className="bg-gray-500">Dismissed</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getSeverityColor = (reportCount) => {
    if (reportCount >= 4) return 'text-red-600 font-bold';
    if (reportCount >= 2) return 'text-orange-600 font-semibold';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Reports & Complaints</h2>
          <p className="text-gray-600">Review and manage user reports</p>
        </div>
        <Button onClick={loadReports} className="bg-green-600 hover:bg-green-700">
          Refresh
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold">{reports.filter(r => r.status === 'pending').length}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Eye className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold">{reports.filter(r => r.status === 'under_review').length}</p>
              <p className="text-sm text-gray-600">Under Review</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold">{reports.filter(r => r.status === 'resolved').length}</p>
              <p className="text-sm text-gray-600">Resolved</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Ban className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-2xl font-bold">{reports.length}</p>
              <p className="text-sm text-gray-600">Total Reports</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by reason, user name, phone..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-10 py-2"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Reports Table */}
      {filteredReports.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reported User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">{report.reported_user_name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{report.reported_user_phone || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md truncate">{report.reason || 'No reason provided'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(report.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedReport(report);
                          setShowModal(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No reports found matching your filters</p>
        </Card>
      )}

      {/* Report Details Modal */}
      {showModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Report Details</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Reported User</p>
                <p className="font-medium">{selectedReport.reported_user_name}</p>
                <p className="text-sm text-gray-500">{selectedReport.reported_user_phone}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Reason</p>
                <p className="font-medium">{selectedReport.reason}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Date Reported</p>
                <p className="font-medium">{new Date(selectedReport.created_at).toLocaleString()}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Status</p>
                {getStatusBadge(selectedReport.status)}
              </div>

              {selectedReport.evidence_url && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Evidence</p>
                  <img 
                    src={`${BACKEND_URL}${selectedReport.evidence_url}`} 
                    alt="Evidence" 
                    className="max-w-full h-auto border rounded"
                  />
                </div>
              )}

              {selectedReport.admin_notes && (
                <div>
                  <p className="text-sm text-gray-600">Admin Notes</p>
                  <p className="font-medium">{selectedReport.admin_notes}</p>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Actions</p>
                <div className="flex gap-3">
                  {selectedReport.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => handleResolveReport(selectedReport.id, 'resolve')}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve
                      </Button>
                      <Button
                        onClick={() => handleResolveReport(selectedReport.id, 'dismiss')}
                        className="flex-1 bg-gray-600 hover:bg-gray-700"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Dismiss
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedReport(null);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
